import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  CheckCircle2,
  Download,
  FileText,
  FolderOpen,
  History,
  MapPin,
  PackageCheck,
  RefreshCcw,
  Save,
  Truck,
  UserRound,
  XCircle
} from 'lucide-react';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import './BonLivraisonDetail.css';

const API_URL = import.meta.env.VITE_API_URL;

const tokenHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const statusLabels = {
  en_preparation: 'En preparation',
  en_cours: 'En cours de livraison',
  livre: 'Livre',
  annule: 'Annule',
  reaffecte: 'Reaffecte'
};

const installLabels = {
  non_installe: 'Livre mais non installe',
  installation_en_attente: 'Installation en attente',
  installe: 'Livre et installe',
  installation_impossible: 'Installation impossible',
  refuse_client: 'Produit refuse',
  retourne: 'Produit retourne'
};

const productStateLabels = {
  normal: 'Normal',
  defectueux: 'Defectueux',
  perdu_casse: 'Perdu / casse',
  retourne: 'Retourne',
  refuse: 'Refuse'
};

const validationOutcomeLabels = {
  installed: 'Installe correctement',
  defectueux: 'Defectueux',
  perdu_casse: 'Perdu / casse',
  retourne: 'Retourne',
  refuse: 'Refuse'
};

const incidentLabels = {
  '': 'Aucun incident',
  defectueux: 'Produit defectueux',
  perdu_casse: 'Perdu ou casse',
  non_installe: 'Non installe'
};

const fmtDate = value => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('fr-FR');
};

const fmtDateTime = value => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('fr-FR');
};

const fmtNumber = value => (Number(value) || 0).toLocaleString('fr-FR');

const getEquipeChauffeurOptions = (equipes = []) => {
  const options = [];
  equipes.forEach(equipe => {
    const equipeId = equipe._id;
    const chefNom = [equipe.chefEquipe?.prenom, equipe.chefEquipe?.nom].filter(Boolean).join(' ').trim();
    if (chefNom) {
      options.push({
        key: `${equipeId}:chefEquipe`,
        equipeId,
        role: 'chefEquipe',
        userId: equipe.chefEquipe?._id || '',
        nomComplet: chefNom,
        label: `${chefNom} - Chef d'equipe`
      });
    }
    [
      ['sousChefEquipe', 'Sous-chef'],
      ['technicien1', 'Technicien 1'],
      ['technicien2', 'Technicien 2']
    ].forEach(([role, roleLabel]) => {
      const nomComplet = String(equipe[role] || '').trim();
      if (nomComplet) {
        options.push({
          key: `${equipeId}:${role}`,
          equipeId,
          role,
          userId: '',
          nomComplet,
          label: `${nomComplet} - ${roleLabel}`
        });
      }
    });
  });
  return options;
};

const BonLivraisonDetail = ({ onNavigate, onLogout, currentPage }) => {
  const { blId } = useParams();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editForm, setEditForm] = useState({});
  const [vehiculesBL, setVehiculesBL] = useState([]);
  const [equipesBL, setEquipesBL] = useState([]);
  const [loadingOptionsBL, setLoadingOptionsBL] = useState(false);
  const [reaffectForm, setReaffectForm] = useState({
    nouveauClientId: '',
    nouveauDossierId: '',
    destination: '',
    adresseLivraison: '',
    raison: ''
  });
  const [productDrafts, setProductDrafts] = useState({});
  const [selectedArticleIds, setSelectedArticleIds] = useState([]);
  const [reaffectLookup, setReaffectLookup] = useState({ loading: false, message: '' });

  const loadDetail = useCallback(async () => {
    if (!blId) return;
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_URL}/documents-financiers/bons-livraison/${blId}`, {
        headers: tokenHeaders()
      });
      const document = response.data.document;
      setDetail(document);
      setEditForm({
        dateCreation: document.dateCreation ? document.dateCreation.slice(0, 10) : '',
        vehiculeId: document.vehiculeId || '',
        chauffeurMembre: document.chauffeurMembre || {},
        chauffeur: document.chauffeur || '',
        matriculeVoiture: document.matriculeVoiture || '',
        dateLivraisonPrevue: document.dateLivraisonPrevue ? document.dateLivraisonPrevue.slice(0, 10) : '',
        adresseLivraison: document.adresseLivraison || '',
        destination: document.destination || '',
        statutLivraison: document.statutLivraison || 'en_preparation',
        notes: document.notes || ''
      });
      const drafts = {};
      (document.articles || []).forEach(article => {
        drafts[article._id] = {
          quantiteLivree: article.quantiteLivree || 0,
          etatProduit: article.etatProduit || 'normal',
          statutInstallation: article.statutInstallation || 'non_installe',
          commentaireLivraison: article.commentaireLivraison || '',
          motifNonInstallation: article.motifNonInstallation || '',
          incident: {
            typeIncident: article.incident?.typeIncident || '',
            cause: article.incident?.cause || '',
            observation: article.incident?.observation || '',
            responsable: article.incident?.responsable || '',
            dateIncident: article.incident?.dateIncident ? article.incident.dateIncident.slice(0, 10) : ''
          }
        };
      });
      setProductDrafts(drafts);
      setSelectedArticleIds((document.articles || []).map(article => article._id));
      return document;
    } catch (err) {
      console.error('Erreur detail BL:', err);
      setError(err.response?.data?.message || 'Impossible de charger le detail du Bon de Livraison.');
    } finally {
      setLoading(false);
    }
  }, [blId]);

  const loadOptionsBL = useCallback(async () => {
    try {
      setLoadingOptionsBL(true);
      const [vehiculesRes, equipesRes] = await Promise.all([
        axios.get(`${API_URL}/vehicules`, { headers: tokenHeaders() }),
        axios.get(`${API_URL}/equipes-installation`, { headers: tokenHeaders() })
      ]);
      setVehiculesBL(Array.isArray(vehiculesRes.data) ? vehiculesRes.data : []);
      setEquipesBL(Array.isArray(equipesRes.data) ? equipesRes.data : []);
    } catch (err) {
      console.error('Erreur options BL:', err);
    } finally {
      setLoadingOptionsBL(false);
    }
  }, []);

  useEffect(() => {
    loadDetail();
    loadOptionsBL();
  }, [loadDetail, loadOptionsBL]);

  const updateEdit = (field, value) => setEditForm(prev => ({ ...prev, [field]: value }));

  const updateEditChauffeur = (key) => {
    const option = getEquipeChauffeurOptions(equipesBL).find(item => item.key === key);
    setEditForm(prev => ({
      ...prev,
      chauffeurMembre: option ? {
        equipeId: option.equipeId,
        role: option.role,
        userId: option.userId,
        nomComplet: option.nomComplet
      } : {}
    }));
  };

  const editChauffeurKey = editForm.chauffeurMembre?.equipeId && editForm.chauffeurMembre?.role
    ? `${editForm.chauffeurMembre.equipeId}:${editForm.chauffeurMembre.role}`
    : '';

  const updateProductDraft = (articleId, field, value) => {
    setProductDrafts(prev => ({
      ...prev,
      [articleId]: {
        ...(prev[articleId] || {}),
        [field]: value
      }
    }));
  };

  const updateIncidentDraft = (articleId, field, value) => {
    setProductDrafts(prev => ({
      ...prev,
      [articleId]: {
        ...(prev[articleId] || {}),
        incident: {
          ...(prev[articleId]?.incident || {}),
          [field]: value
        }
      }
    }));
  };

  const updateValidationOutcome = (article, outcome) => {
    const qte = Number(article.quantitePrevue || article.quantite) || 0;
    const presets = {
      installed: {
        quantiteLivree: qte,
        etatProduit: 'normal',
        statutInstallation: 'installe',
        incident: { typeIncident: '' }
      },
      defectueux: {
        quantiteLivree: qte,
        etatProduit: 'defectueux',
        statutInstallation: 'installation_impossible',
        incident: { typeIncident: 'defectueux' }
      },
      perdu_casse: {
        quantiteLivree: qte,
        etatProduit: 'perdu_casse',
        statutInstallation: 'installation_impossible',
        incident: { typeIncident: 'perdu_casse' }
      },
      retourne: {
        quantiteLivree: qte,
        etatProduit: 'retourne',
        statutInstallation: 'retourne',
        incident: { typeIncident: '' }
      },
      refuse: {
        quantiteLivree: qte,
        etatProduit: 'refuse',
        statutInstallation: 'refuse_client',
        incident: { typeIncident: '' }
      }
    };

    const preset = presets[outcome];
    if (!preset) return;
    setProductDrafts(prev => ({
      ...prev,
      [article._id]: {
        ...(prev[article._id] || {}),
        ...preset,
        incident: {
          ...(prev[article._id]?.incident || {}),
          ...(preset.incident || {})
        }
      }
    }));
  };

  const buildValidationArticlePayload = (article) => ({
    articleId: article._id,
    ...(productDrafts[article._id] || {})
  });

  const buildArticlePayload = (article) => ({
    produitId: article.produitId,
    produitClientId: article.produitClientId,
    clientId: article.clientId || detail.clientId,
    dossierId: article.dossierId || detail.dossierId,
    paletteId: article.paletteId || '',
    numerosSerie: article.numerosSerie || [],
    designation: article.designation || article.nomProduit,
    nomProduit: article.nomProduit,
    quantite: Number(article.quantite || article.quantitePrevue) || 0,
    prixUnitaire: Number(article.prixUnitaire) || 0,
    uniteMesure: article.uniteMesure || 'unite',
    specifications: article.specifications || '',
    tva: Number(article.tva) || 19,
    remise: Number(article.remise) || 0,
    montantHT: Number(article.montantHT) || 0,
    montantTTC: Number(article.montantTTC) || 0
  });

  const toggleArticleSelection = (articleId) => {
    setSelectedArticleIds(prev =>
      prev.includes(articleId)
        ? prev.filter(id => id !== articleId)
        : [...prev, articleId]
    );
  };

  const selectAllArticles = () => {
    setSelectedArticleIds((detail?.articles || []).map(article => article._id));
  };

  const clearArticleSelection = () => {
    setSelectedArticleIds([]);
  };

  const saveGeneral = async () => {
    try {
      setSaving(true);
      await axios.put(`${API_URL}/documents-financiers/bons-livraison/${blId}`, editForm, {
        headers: tokenHeaders()
      });
      await loadDetail();
    } catch (err) {
      const errors = err.response?.data?.errors;
      alert(errors?.length ? errors.join('\n') : (err.response?.data?.message || 'Modification impossible.'));
    } finally {
      setSaving(false);
    }
  };

  const saveProduct = async (articleId) => {
    try {
      setSaving(true);
      await axios.patch(
        `${API_URL}/documents-financiers/bons-livraison/${blId}/articles/${articleId}`,
        productDrafts[articleId],
        { headers: tokenHeaders() }
      );
      await loadDetail();
    } catch (err) {
      alert(err.response?.data?.message || 'Mise a jour produit impossible.');
    } finally {
      setSaving(false);
    }
  };

  const validateDocument = async () => {
    if (!window.confirm(`Valider le Bon de Livraison ${detail?.numero || ''} ?`)) return;
    try {
      setSaving(true);
      await axios.patch(
        `${API_URL}/documents-financiers/${blId}/valider`,
        { articlesValidation: (detail?.articles || []).map(buildValidationArticlePayload) },
        { headers: tokenHeaders() }
      );
      await loadDetail();
    } catch (err) {
      const errors = err.response?.data?.errors;
      alert(errors?.length ? errors.join('\n') : (err.response?.data?.message || 'Validation impossible.'));
    } finally {
      setSaving(false);
    }
  };

  const validateSelectedProducts = async (mode = 'selected') => {
    if (!detail?.articles?.length) return;
    const selectedIds = mode === 'all'
      ? detail.articles.map(article => article._id)
      : selectedArticleIds;

    if (selectedIds.length === 0) {
      alert('Veuillez selectionner au moins un produit a valider.');
      return;
    }

    const selectedArticles = detail.articles.filter(article => selectedIds.includes(article._id));
    const allSelected = selectedArticles.length === detail.articles.length;
    const label = allSelected ? 'tous les produits' : `${selectedArticles.length} produit(s) selectionne(s)`;

    if (!window.confirm(`Valider le BL ${detail.numero} avec ${label} ?`)) return;

    try {
      setSaving(true);
      let documentForValidation = detail;
      if (!allSelected) {
        await axios.put(
          `${API_URL}/documents-financiers/bons-livraison/${blId}`,
          {
            articles: selectedArticles.map(article => ({
              ...buildArticlePayload(article),
              ...(productDrafts[article._id] || {})
            })),
            motif: 'Validation BL avec selection de produits'
          },
          { headers: tokenHeaders() }
        );
        documentForValidation = await loadDetail();
      }
      await axios.patch(
        `${API_URL}/documents-financiers/${blId}/valider`,
        { articlesValidation: (documentForValidation?.articles || selectedArticles).map(buildValidationArticlePayload) },
        { headers: tokenHeaders() }
      );
      await loadDetail();
    } catch (err) {
      const errors = err.response?.data?.errors;
      alert(errors?.length ? errors.join('\n') : (err.response?.data?.message || 'Validation impossible.'));
    } finally {
      setSaving(false);
    }
  };

  const cancelDocument = async () => {
    const motif = window.prompt('Motif de l annulation du BL');
    if (!motif) return;
    try {
      setSaving(true);
      await axios.patch(`${API_URL}/documents-financiers/bons-livraison/${blId}/annuler`, { motif }, { headers: tokenHeaders() });
      await loadDetail();
    } catch (err) {
      alert(err.response?.data?.message || 'Annulation impossible.');
    } finally {
      setSaving(false);
    }
  };

  const reassignDocument = async () => {
    try {
      setSaving(true);
      await axios.patch(`${API_URL}/documents-financiers/bons-livraison/${blId}/reaffecter`, reaffectForm, {
        headers: tokenHeaders()
      });
      setReaffectForm({ nouveauClientId: '', nouveauDossierId: '', destination: '', adresseLivraison: '', raison: '' });
      await loadDetail();
    } catch (err) {
      alert(err.response?.data?.message || 'Reaffectation impossible.');
    } finally {
      setSaving(false);
    }
  };

  const hydrateReaffectDestination = useCallback(async () => {
    if (!reaffectForm.nouveauClientId || !reaffectForm.nouveauDossierId) return;

    try {
      setReaffectLookup({ loading: true, message: 'Recuperation destination...' });
      const response = await axios.get(`${API_URL}/clients/${reaffectForm.nouveauClientId}`, {
        headers: tokenHeaders()
      });
      const client = response.data.client || response.data;
      const dossier = (client.dossiers || []).find(item => String(item._id) === String(reaffectForm.nouveauDossierId));
      if (!dossier) {
        setReaffectLookup({ loading: false, message: 'Dossier destination introuvable pour ce client.' });
        return;
      }

      const reference = dossier.referenceSteg || dossier.moisAnnee || dossier._id;
      const clientNom = [client.prenom, client.nom].filter(Boolean).join(' ').trim() || client.nom || '';
      const destinationAuto = [clientNom, reference ? `Dossier ${reference}` : ''].filter(Boolean).join(' - ');
      const adresseAuto = dossier.adresseLivraison || dossier.adresse || dossier.destination || client.adresse || '';

      setReaffectForm(prev => ({
        ...prev,
        destination: destinationAuto,
        adresseLivraison: adresseAuto
      }));
      setReaffectLookup({ loading: false, message: 'Destination et adresse recuperees depuis le dossier.' });
    } catch (err) {
      console.error('Erreur recuperation dossier destination:', err);
      setReaffectLookup({ loading: false, message: 'Impossible de recuperer le dossier destination.' });
    }
  }, [reaffectForm.nouveauClientId, reaffectForm.nouveauDossierId]);

  useEffect(() => {
    hydrateReaffectDestination();
  }, [hydrateReaffectDestination]);

  const openPdf = () => {
    window.open(`${API_URL}/documents-financiers/${blId}/pdf?token=${localStorage.getItem('token')}`, '_blank');
  };

  const downloadPdf = async () => {
    try {
      const response = await axios.get(`${API_URL}/documents-financiers/${blId}/pdf`, {
        headers: tokenHeaders(),
        responseType: 'blob'
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${detail?.numero || 'bon-livraison'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      alert(err.response?.data?.message || 'Telechargement du PDF impossible.');
    }
  };

  const goBack = () => {
    if (onNavigate) onNavigate('dashboard-bons-livraison');
  };

  const goToClientDossier = () => {
    if (!detail?.clientId || !detail?.dossierId) return;
    if (onNavigate) {
      onNavigate('detail-dossier', { clientId: detail.clientId, dossierId: detail.dossierId });
      return;
    }
    const qs = new URLSearchParams({ clientId: detail.clientId, dossierId: detail.dossierId }).toString();
    window.location.href = `/detail-dossier?${qs}`;
  };

  const totals = {
    lignes: detail?.articles?.length || 0,
    prevue: (detail?.articles || []).reduce((sum, item) => sum + (Number(item.quantitePrevue) || 0), 0),
    livree: (detail?.articles || []).reduce((sum, item) => sum + (Number(item.quantiteLivree) || 0), 0),
    nonInstallee: (detail?.articles || []).reduce((sum, item) => sum + (item.statutInstallation === 'installe' ? 0 : (Number(item.quantitePrevue) || 0)), 0)
  };

  return (
    <div className="bldetail-layout">
      <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
      <main className="bldetail-content">
        {loading ? (
          <LoadingSpinner message="Chargement du Bon de Livraison..." />
        ) : error ? (
          <div className="bldetail-error">{error}</div>
        ) : (
          <>
            <section className="bldetail-header">
              <button className="bldetail-back" onClick={goBack}><ArrowLeft size={18} /> Retour</button>
              <div>
                <span>Bon de Livraison</span>
                <h1>{detail.numero}</h1>
                <p>{detail.clientNom || 'Client'} - Dossier {detail.referenceDossier || '-'}</p>
              </div>
              <div className="bldetail-header-actions">
                <span className={`bldetail-status ${detail.statutLivraison}`}>{statusLabels[detail.statutLivraison] || detail.statutLivraison}</span>
                <button className="refresh" title="Actualiser" onClick={loadDetail}><RefreshCcw size={18} /></button>
                <button className="dossier-link" title="Voir le dossier client" onClick={goToClientDossier} disabled={!detail.clientId || !detail.dossierId}>
                  <FolderOpen size={18} />
                  <span>Dossier client</span>
                </button>
                <button className="pdf" title="Voir PDF" onClick={openPdf}><FileText size={18} /></button>
                <button className="download" title="Telecharger PDF" onClick={downloadPdf}><Download size={18} /></button>
                {detail.statut !== 'valide' && <button className="validate" title="Valider" onClick={validateDocument}><CheckCircle2 size={18} /></button>}
                {detail.statutLivraison !== 'annule' && <button className="cancel" title="Annuler" onClick={cancelDocument}><XCircle size={18} /></button>}
              </div>
            </section>

            <section className="bldetail-kpis">
              <div><FileText size={18} /><span>Lignes</span><strong>{fmtNumber(totals.lignes)}</strong></div>
              <div><PackageCheck size={18} /><span>Quantite prevue</span><strong>{fmtNumber(totals.prevue)}</strong></div>
              <div><Truck size={18} /><span>Quantite livree</span><strong>{fmtNumber(totals.livree)}</strong></div>
              <div><XCircle size={18} /><span>Non installes</span><strong>{fmtNumber(totals.nonInstallee)}</strong></div>
            </section>

            <section className="bldetail-download-panel">
              <div>
                <FileText size={20} />
                <div>
                  <strong>Document PDF du Bon de Livraison</strong>
                  <span>Consulter ou telecharger le BL {detail.numero}</span>
                </div>
              </div>
              <div>
                <button className="bldetail-secondary" onClick={openPdf}><FileText size={16} /> Voir PDF</button>
                <button className="bldetail-primary no-margin" onClick={downloadPdf}><Download size={16} /> Telecharger PDF</button>
              </div>
            </section>

            {detail.statut !== 'valide' && (
              <section className="bldetail-validation-panel">
                <div>
                  <CheckCircle2 size={20} />
                  <div>
                    <strong>Validation du Bon de Livraison</strong>
                    <span>{selectedArticleIds.length} produit(s) selectionne(s) sur {detail.articles?.length || 0}</span>
                  </div>
                </div>
                <div className="bldetail-validation-actions">
                  <button type="button" onClick={selectAllArticles}>Tout selectionner</button>
                  <button type="button" onClick={clearArticleSelection}>Vider</button>
                  <button type="button" className="selected" onClick={() => validateSelectedProducts('selected')} disabled={saving || selectedArticleIds.length === 0}>
                    Valider selection
                  </button>
                  <button type="button" className="all" onClick={() => validateSelectedProducts('all')} disabled={saving}>
                    Valider tous les produits
                  </button>
                </div>
              </section>
            )}

            <section className="bldetail-main-grid">
              <div className="bldetail-panel">
                <div className="bldetail-panel-title"><Truck size={18} /><h2>Informations generales</h2></div>
                <div className="bldetail-info-grid">
                  <div><span>Date du BL</span><strong>{fmtDate(detail.dateCreation)}</strong></div>
                  <div><span>Date prevue</span><strong>{fmtDate(detail.dateLivraisonPrevue)}</strong></div>
                  <div><span>Reference dossier</span><strong>{detail.referenceDossier || '-'}</strong></div>
                  <div><span>Document</span><strong>{detail.statut}</strong></div>
                  <div><span>Stock</span><strong>{detail.stockDeduit ? 'Deduit' : 'Reserve'}</strong></div>
                  <div><span>Chauffeur</span><strong>{detail.chauffeur || '-'}</strong></div>
                  <div><span>Vehicule</span><strong>{detail.matriculeVoiture || '-'}</strong></div>
                  <div><span>Adresse</span><strong>{detail.adresseLivraison || '-'}</strong></div>
                  <div><span>Destination</span><strong>{detail.destination || '-'}</strong></div>
                </div>
              </div>

              <div className="bldetail-panel">
                <div className="bldetail-panel-title"><Save size={18} /><h2>Modifier avant validation</h2></div>
                <div className="bldetail-form-grid">
                  <label>Date du BL<input type="date" value={editForm.dateCreation || ''} onChange={e => updateEdit('dateCreation', e.target.value)} /></label>
                  <label>Vehicule
                    <select value={editForm.vehiculeId || ''} onChange={e => updateEdit('vehiculeId', e.target.value)} disabled={loadingOptionsBL}>
                      <option value="">Selectionner un vehicule</option>
                      {vehiculesBL.map(vehicule => (
                        <option key={vehicule._id} value={vehicule._id}>
                          {vehicule.matricule} - {vehicule.marque || vehicule.type || 'Vehicule'}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>Chauffeur
                    <select value={editChauffeurKey} onChange={e => updateEditChauffeur(e.target.value)} disabled={loadingOptionsBL}>
                      <option value="">Selectionner un chauffeur</option>
                      {getEquipeChauffeurOptions(equipesBL).map(option => (
                        <option key={option.key} value={option.key}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  <label>Date livraison<input type="date" value={editForm.dateLivraisonPrevue || ''} onChange={e => updateEdit('dateLivraisonPrevue', e.target.value)} /></label>
                  <label>Statut livraison
                    <select value={editForm.statutLivraison || 'en_preparation'} onChange={e => updateEdit('statutLivraison', e.target.value)}>
                      <option value="en_preparation">En preparation</option>
                      <option value="en_cours">En cours</option>
                      <option value="livre">Livre</option>
                      <option value="reaffecte">Reaffecte</option>
                      <option value="annule">Annule</option>
                    </select>
                  </label>
                  <label>Destination<input value={editForm.destination || ''} onChange={e => updateEdit('destination', e.target.value)} /></label>
                  <label>Adresse<input value={editForm.adresseLivraison || ''} onChange={e => updateEdit('adresseLivraison', e.target.value)} /></label>
                  <label className="wide">Notes<textarea value={editForm.notes || ''} onChange={e => updateEdit('notes', e.target.value)} /></label>
                </div>
                <button className="bldetail-primary" onClick={saveGeneral} disabled={saving}>
                  <Save size={16} /> Enregistrer les informations
                </button>
              </div>
            </section>

            <section className="bldetail-panel">
              <div className="bldetail-panel-title"><PackageCheck size={18} /><h2>Produits du Bon de Livraison</h2></div>
              <div className="bldetail-products">
                {(detail.articles || []).map(article => {
                  const draft = productDrafts[article._id] || {};
                  return (
                    <article className="bldetail-product" key={article._id}>
                      <div className="bldetail-product-head">
                        {detail.statut !== 'valide' && (
                          <label className="bldetail-product-check">
                            <input
                              type="checkbox"
                              checked={selectedArticleIds.includes(article._id)}
                              onChange={() => toggleArticleSelection(article._id)}
                            />
                          </label>
                        )}
                        <div>
                          <strong>{article.nomProduit}</strong>
                          <span>{article.categorie} · Ref {article.reference || '-'}</span>
                        </div>
                        <span className="bldetail-stock">{article.statutStock}</span>
                      </div>
                      <div className="bldetail-product-meta">
                        <div><span>Quantite prevue</span><strong>{fmtNumber(article.quantitePrevue)}</strong></div>
                        <label>Quantite livree<input type="number" min="0" value={draft.quantiteLivree ?? 0} onChange={e => updateProductDraft(article._id, 'quantiteLivree', e.target.value)} /></label>
                        <div className="bldetail-outcome">
                          <span>Etat apres livraison</span>
                          <div>
                            {Object.entries(validationOutcomeLabels).map(([value, label]) => (
                              <button
                                type="button"
                                key={value}
                                onClick={() => updateValidationOutcome(article, value)}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <label>Installation
                          <select value={draft.statutInstallation || 'non_installe'} onChange={e => updateProductDraft(article._id, 'statutInstallation', e.target.value)}>
                            {Object.entries(installLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                          </select>
                        </label>
                        <label>Etat produit
                          <select value={draft.etatProduit || 'normal'} onChange={e => updateProductDraft(article._id, 'etatProduit', e.target.value)}>
                            {Object.entries(productStateLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                          </select>
                        </label>
                      </div>
                      <div className="bldetail-series">
                        <span>Numeros de serie</span>
                        <strong>{(article.numerosSerie || []).join(', ') || '-'}</strong>
                      </div>
                      <div className="bldetail-product-notes">
                        <label>Motif non installation<textarea value={draft.motifNonInstallation || ''} onChange={e => updateProductDraft(article._id, 'motifNonInstallation', e.target.value)} /></label>
                        <label>Commentaire livraison<textarea value={draft.commentaireLivraison || ''} onChange={e => updateProductDraft(article._id, 'commentaireLivraison', e.target.value)} /></label>
                      </div>
                      <div className="bldetail-incident">
                        <label>Incident
                          <select value={draft.incident?.typeIncident || ''} onChange={e => updateIncidentDraft(article._id, 'typeIncident', e.target.value)}>
                            {Object.entries(incidentLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                          </select>
                        </label>
                        <label>Cause<input value={draft.incident?.cause || ''} onChange={e => updateIncidentDraft(article._id, 'cause', e.target.value)} /></label>
                        <label>Responsable<input value={draft.incident?.responsable || ''} onChange={e => updateIncidentDraft(article._id, 'responsable', e.target.value)} /></label>
                        <label>Date incident<input type="date" value={draft.incident?.dateIncident || ''} onChange={e => updateIncidentDraft(article._id, 'dateIncident', e.target.value)} /></label>
                        <label className="wide">Observation<textarea value={draft.incident?.observation || ''} onChange={e => updateIncidentDraft(article._id, 'observation', e.target.value)} /></label>
                      </div>
                      <button className="bldetail-secondary" onClick={() => saveProduct(article._id)} disabled={saving}>
                        <Save size={16} /> Enregistrer ce produit
                      </button>
                    </article>
                  );
                })}
              </div>
            </section>

            <section className="bldetail-main-grid">
              <div className="bldetail-panel">
                <div className="bldetail-panel-title"><MapPin size={18} /><h2>Reaffectation</h2></div>
                <div className="bldetail-form-grid">
                  <label>Nouveau client ID<input value={reaffectForm.nouveauClientId} onChange={e => setReaffectForm(prev => ({ ...prev, nouveauClientId: e.target.value }))} /></label>
                  <label>Nouveau dossier ID<input value={reaffectForm.nouveauDossierId} onChange={e => setReaffectForm(prev => ({ ...prev, nouveauDossierId: e.target.value }))} /></label>
                  <label>Destination<input value={reaffectForm.destination} readOnly placeholder="Recuperee automatiquement" /></label>
                  <label>Adresse<input value={reaffectForm.adresseLivraison} readOnly placeholder="Recuperee automatiquement" /></label>
                  <label className="wide">Raison<textarea value={reaffectForm.raison} onChange={e => setReaffectForm(prev => ({ ...prev, raison: e.target.value }))} /></label>
                </div>
                <p className={`bldetail-reaffect-hint ${reaffectLookup.loading ? 'loading' : ''}`}>
                  {reaffectLookup.message || 'La destination et l adresse seront recuperees automatiquement depuis le dossier cible.'}
                </p>
                <button className="bldetail-warning" onClick={reassignDocument} disabled={saving}>Reaffecter ce BL</button>
              </div>

              <div className="bldetail-panel">
                <div className="bldetail-panel-title"><History size={18} /><h2>Historique systeme</h2></div>
                <div className="bldetail-history">
                  {(detail.historique || []).slice().reverse().map((item, index) => (
                    <div key={`${item.date}-${index}`}>
                      <span>{fmtDateTime(item.date)}</span>
                      <strong>{item.typeActionDetail || item.action}</strong>
                      <p>{item.commentaire || '-'}</p>
                      <small><UserRound size={13} /> {item.utilisateur ? `${item.utilisateur.prenom || ''} ${item.utilisateur.nom || ''}`.trim() : '-'}</small>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default BonLivraisonDetail;
